import * as React from 'react';
import type { AudioAsset, Row } from '@tekvoice/shared';

export interface AbDialogPayload {
  rowId: string;
  assetId: string;
  language: 'EN' | 'AR';
  newTakeId: string;
  newTakeUrl: string | null;
  previousTakeId: string | null;
  previousTakeUrl: string | null;
  suggestOverride: boolean;
}

interface ReviewContextValue {
  batchId: string;
  /** Write a fresh row back into the list cache (after an edit/approve/regenerate). */
  applyRow: (row: Row) => void;
  applyAsset: (rowId: string, asset: AudioAsset) => void;
  /** Refetch batch + rows (counts, takes, progress). */
  invalidate: () => void;
  canEdit: boolean;
  canApprove: boolean;
  canUnapprove: boolean;
  canRegenerate: boolean;
  /** The RUNNING run, if any — a PENDING asset is only untouchable while one owns it (m4). */
  activeRunId: string | null;
  /** m2 — a soft-deleted batch is read-only: nothing may be edited, generated or published. */
  batchDeleted: boolean;
  openHistory: (row: Row) => void;
  openAb: (payload: AbDialogPayload) => void;
  /** Opens the "Fix pronunciation" panel for a row (or closes it with `null`). */
  openOverride: (rowId: string | null) => void;
  overrideRowId: string | null;
}

const ReviewContext = React.createContext<ReviewContextValue | null>(null);

export function ReviewProvider({
  value,
  children,
}: {
  value: ReviewContextValue;
  children: React.ReactNode;
}): React.JSX.Element {
  return <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>;
}

export function useReview(): ReviewContextValue {
  const context = React.useContext(ReviewContext);
  if (!context) throw new Error('useReview must be used inside <ReviewProvider>');
  return context;
}
